/**
 * Machine à états des documents commerciaux (ADR-08).
 *
 * Après validation, un document est IMMUABLE : la seule correction possible
 * est l'émission d'un avoir. C'est une exigence comptable, et c'est aussi ce
 * qui supprime la quasi-totalité des conflits de synchronisation hors ligne —
 * un document figé ne peut pas diverger entre deux appareils.
 */
export const DOCUMENT_TYPES = ['quote', 'invoice', 'deposit_invoice', 'credit_note', 'order'];
export const DOCUMENT_STATUSES = [
    'draft',
    'validated',
    'partially_paid',
    'paid',
    'cancelled',
];
const TRANSITIONS = {
    draft: ['validated', 'cancelled'],
    validated: ['partially_paid', 'paid', 'cancelled'],
    partially_paid: ['paid', 'cancelled'],
    paid: [],
    cancelled: [],
};
export function canTransition(from, to) {
    return TRANSITIONS[from].includes(to);
}
export function assertTransition(from, to) {
    if (!canTransition(from, to)) {
        throw new Error(`Transition interdite : ${from} → ${to}`);
    }
}
/** Un document non brouillon ne peut plus être modifié (ADR-08). */
export function isMutable(status) {
    return status === 'draft';
}
/**
 * Le même document, vu par un ADMINISTRATEUR de la boutique (ADR-08 amendé).
 *
 * ⭐ L'immuabilité tenait le commerçant en échec sur un cas banal : une erreur
 * de saisie constatée dans la minute, sur une vente déjà validée. L'avoir est
 * la bonne réponse comptable pour un remboursement, pas pour un libellé faux
 * ou une quantité de trop — il laisse deux documents là où il fallait corriger
 * un chiffre. L'admin peut donc modifier, et lui seul.
 *
 * ⚠️ Ce qui est levé, c'est l'interdiction — PAS la traçabilité. Toute
 * modification archive l'état d'avant (`transaction_revisions`) et exige un
 * motif. C'est la contrepartie qui rend l'amendement défendable : la facture
 * reste reconstituable, ce que l'immuabilité garantissait gratuitement.
 *
 * ⚠️ Un document ANNULÉ reste figé pour tout le monde. Le rouvrir reviendrait
 * à ressusciter une vente dont le stock a déjà été rendu, et à faire mentir
 * l'annulation auprès de qui l'a constatée.
 */
export function isMutableParAdmin(status) {
    return status !== 'cancelled';
}
/** Seuls les brouillons sont supprimables ; le reste se corrige par avoir. */
export function isDeletable(status) {
    return status === 'draft';
}
/** Un numéro légal n'est attribué qu'à la validation (ADR-12). */
export function requiresNumber(status) {
    return status !== 'draft';
}
/**
 * Statut déduit du règlement (RM-5). Le statut n'est jamais saisi à la main :
 * il découle des montants, ce qui le rend recalculable après synchronisation.
 */
export function statusFromPayment(totalAmount, paidAmount, current) {
    if (current === 'draft' || current === 'cancelled')
        return current;
    if (paidAmount <= 0)
        return 'validated';
    if (paidAmount >= totalAmount)
        return 'paid';
    return 'partially_paid';
}
/**
 * Les types de document qui déclenchent un mouvement de stock à la validation
 * (RM-7).
 *
 * ⭐ La COMMANDE en fait partie. Passer commande sur un article en rupture doit
 * RÉSERVER la quantité — la sortir du disponible, quitte à passer en négatif —
 * pour que « combien manque-t-il pour honorer ce qui est déjà promis ? » se
 * lise directement dans le stock, sans recompter les commandes une par une.
 *
 * ⚠️ La facture née d'une commande NE REJOUE PAS ce mouvement : la réservation
 * a déjà eu lieu à la commande, et une seconde sortie compterait les mêmes
 * articles deux fois. C'est `validerDocument` qui porte cette exception,
 * localement — `affectsStock` reste la règle générale, par type seul.
 */
export function affectsStock(type) {
    return type === 'invoice' || type === 'credit_note' || type === 'order';
}
/** Un avoir référence toujours le document qu'il corrige (RM-6). */
export function requiresParent(type) {
    return type === 'credit_note';
}
//# sourceMappingURL=document.js.map